import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OkAloneService } from '../../../ok-alone.service';

interface AppLinkSection {
  section_id: number;
  company_id: number;
  title: string;
  created_at: string;
  link_count: number;
}

interface AppLink {
  link_id: number;
  section_id: number;
  company_id: number;
  name: string;
  url: string;
  created_at: string;
  section_title: string;
}

@Component({
  selector: 'app-app-links',
  templateUrl: './app-links.component.html',
  styleUrls: ['./app-links.component.scss']
})
export class AppLinksComponent implements OnInit {

  // Sections with their links
  sections: AppLinkSection[] = [];
  sectionsLoading = false;
  sectionForm: FormGroup;
  editingSectionId: number | null = null;
  showSectionModal = false;

  // Links (grouped by section)
  linksBySection: { [sectionId: number]: AppLink[] } = {};
  linksLoading = false;
  linkForm: FormGroup;
  editingLinkId: number | null = null;
  showLinkModal = false;

  // Search and Filters
  searchTerm = '';
  sortOrder = 'newest';

  constructor(
    private okService: OkAloneService,
    private fb: FormBuilder
  ) {
    // Initialize forms
    this.sectionForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(255)]]
    });

    this.linkForm = this.fb.group({
      section_id: ['', [Validators.required]],
      name: ['', [Validators.required, Validators.maxLength(255)]],
      url: ['', [Validators.required, Validators.maxLength(2048)]]
    });
  }

  ngOnInit(): void {
    this.loadSections();
    this.loadLinks();
  }

  // Sections Management
  loadSections() {
    this.sectionsLoading = true;
    this.okService.get('applinksections', {
      search: this.searchTerm,
      sort: this.sortOrder
    }).subscribe({
      next: (response: any) => {
        this.sections = response.body || [];
        this.sectionsLoading = false;
      },
      error: (error) => {
        console.error('Error loading sections:', error);
        this.okService.setAlert('Error loading sections', 'danger');
        this.sectionsLoading = false;
      }
    });
  }

  openSectionModal(section: AppLinkSection | null = null) {
    if (section) {
      // Edit mode
      this.editingSectionId = section.section_id;
      this.sectionForm.patchValue({
        title: section.title
      });
    } else {
      // Create mode
      this.editingSectionId = null;
      this.sectionForm.reset();
    }
    this.showSectionModal = true;
  }

  closeSectionModal() {
    this.showSectionModal = false;
    this.editingSectionId = null;
    this.sectionForm.reset();
  }

  saveSection() {
    if (this.sectionForm.valid) {
      const formData = this.sectionForm.value;
      
      if (this.editingSectionId) {
        // Update existing section
        this.okService.put('applinksections/' + this.editingSectionId, formData).subscribe({
          next: (response: any) => {
            this.okService.setAlert('Section updated successfully', 'success');
            this.loadSections();
            this.closeSectionModal();
          },
          error: (error) => {
            console.error('Error updating section:', error);
            this.okService.setAlert('Error updating section', 'danger');
          }
        });
      } else {
        // Create new section
        this.okService.post('applinksections', formData).subscribe({
          next: (response: any) => {
            this.okService.setAlert('Section created successfully', 'success');
            this.loadSections();
            this.closeSectionModal();
          },
          error: (error) => {
            console.error('Error creating section:', error);
            this.okService.setAlert('Error creating section', 'danger');
          }
        });
      }
    }
  }

  deleteSection(section: AppLinkSection) {
    if (confirm(`Are you sure you want to delete "${section.title}"? This will also delete all links in this section.`)) {
      this.okService.delete('applinksections/' + section.section_id).subscribe({
        next: (response: any) => {
          this.okService.setAlert('Section deleted successfully', 'success');
          this.loadSections();
          this.loadLinks(); // Refresh links as well
        },
        error: (error) => {
          console.error('Error deleting section:', error);
          this.okService.setAlert('Error deleting section', 'danger');
        }
      });
    }
  }

  // Links Management
  loadLinks() {
    this.linksLoading = true;
    const params: any = {
      search: this.searchTerm,
      sort: this.sortOrder
    };

    this.okService.get('applinks', params).subscribe({
      next: (response: any) => {
        const allLinks = response.body || [];
        // Group links by section
        this.linksBySection = {};
        allLinks.forEach((link: AppLink) => {
          if (!this.linksBySection[link.section_id]) {
            this.linksBySection[link.section_id] = [];
          }
          this.linksBySection[link.section_id].push(link);
        });
        this.linksLoading = false;
      },
      error: (error) => {
        console.error('Error loading links:', error);
        this.okService.setAlert('Error loading links', 'danger');
        this.linksLoading = false;
      }
    });
  }

  openLinkModal(link: AppLink | null = null, sectionId: number | null = null) {
    if (link) {
      // Edit mode
      this.editingLinkId = link.link_id;
      this.linkForm.patchValue({
        section_id: link.section_id,
        name: link.name,
        url: link.url
      });
    } else {
      // Create mode
      this.editingLinkId = null;
      this.linkForm.reset();
      if (sectionId) {
        this.linkForm.patchValue({ section_id: sectionId });
      }
    }
    this.showLinkModal = true;
  }

  closeLinkModal() {
    this.showLinkModal = false;
    this.editingLinkId = null;
    this.linkForm.reset();
  }

  saveLink() {
    if (this.linkForm.valid) {
      const formData = this.linkForm.value;
      
      if (this.editingLinkId) {
        // Update existing link
        this.okService.put('applinks/' + this.editingLinkId, formData).subscribe({
          next: (response: any) => {
            this.okService.setAlert('Link updated successfully', 'success');
            this.loadLinks();
            this.loadSections(); // Refresh sections to update link counts
            this.closeLinkModal();
          },
          error: (error) => {
            console.error('Error updating link:', error);
            this.okService.setAlert('Error updating link', 'danger');
          }
        });
      } else {
        // Create new link
        this.okService.post('applinks', formData).subscribe({
          next: (response: any) => {
            this.okService.setAlert('Link created successfully', 'success');
            this.loadLinks();
            this.loadSections(); // Refresh sections to update link counts
            this.closeLinkModal();
          },
          error: (error) => {
            console.error('Error creating link:', error);
            this.okService.setAlert('Error creating link', 'danger');
          }
        });
      }
    }
  }

  deleteLink(link: AppLink) {
    if (confirm(`Are you sure you want to delete "${link.name}"?`)) {
      this.okService.delete('applinks/' + link.link_id).subscribe({
        next: (response: any) => {
          this.okService.setAlert('Link deleted successfully', 'success');
          this.loadLinks();
          this.loadSections(); // Refresh sections to update link counts
        },
        error: (error) => {
          console.error('Error deleting link:', error);
          this.okService.setAlert('Error deleting link', 'danger');
        }
      });
    }
  }

  // Filter and Search
  onSearchChange() {
    this.loadSections();
    this.loadLinks();
  }

  onSortChange() {
    this.loadSections();
    this.loadLinks();
  }

  // Utility Methods
  getSectionName(sectionId: number): string {
    const section = this.sections.find(s => s.section_id === sectionId);
    return section ? section.title : 'Unknown Section';
  }

  getLinksForSection(sectionId: number): AppLink[] {
    return this.linksBySection[sectionId] || [];
  }

  openExternalLink(url: string) {
    window.open(url, '_blank');
  }
}
